import { axiosPortalInstance } from '@mais-retorno-front/configs/axios-config';
import { getVideoThumbFromYoutube } from '@mais-retorno-front/helpers/video-thumb';
import {
  IPodcastReturnedByTerm,
  IPostReturnedByTerm,
  IVideoReturnedByTerm,
  ITermReturnedByText,
} from '@mais-retorno-front/models/unified-search.model';

interface IGetPodcastsBySearchTermResponse {
  nodes: IPodcastReturnedByTerm[];
  pageInfo: {
    offsetPagination: {
      total: number;
    };
  };
}

interface IGetPostsBySearchTermResponse {
  nodes: IPostReturnedByTerm[];
  pageInfo: {
    offsetPagination: {
      total: number;
    };
  };
}

interface IGetVideosBySearchTermResponse {
  nodes: IVideoReturnedByTerm[];
  pageInfo: {
    offsetPagination: {
      total: number;
    };
  };
}

interface IGetTermsBySearchTermResponse {
  nodes: ITermReturnedByText[];
  pageInfo: {
    offsetPagination: {
      total: number;
    };
  };
}

export const getPostsBySearchTerm = async (
  searchTerm: string
): Promise<IGetPostsBySearchTermResponse> => {
  try {
    const { data } = await axiosPortalInstance({
      method: 'POST',
      data: {
        query: `
        query searchNews {
          search(query: "${searchTerm}") {
            noticias {
              data {
                attributes {
                  title
                  slug
                  publishedAt
                  excerpt
                  chapeu
                  thumbnail {
                    data {
                      attributes {
                        name
                        alternativeText
                        url
                      }
                    }
                  }
                  categories {
                    data {
                      attributes {
                        title
                      }
                    }
                  }
                }
              }
            }
          }
        }
        `,
      },
    });

    return {
      nodes: data?.data?.search?.noticias?.data?.map((news) => {
        return {
          title: news?.attributes?.title || null,
          slug: news?.attributes?.slug || null,
          date: news?.attributes?.publishedAt || null,
          excerpt: news?.attributes?.excerpt || null,
          chapeu: news?.attributes?.chapeu || null,
          featuredImage: {
            node: {
              sourceUrl:
                news?.attributes?.thumbnail?.data?.attributes?.url ||
                '/img/placeholder.png',
              title:
                news?.attributes?.thumbnail?.data?.attributes?.name ||
                news?.attributes?.thumbnail?.data?.attributes
                  ?.alternativeText ||
                null,
              altText:
                news?.attributes?.thumbnail?.data?.attributes
                  ?.alternativeText ||
                news?.attributes?.thumbnail?.data?.attributes?.name ||
                null,
            },
          },
          categories: {
            nodes: {
              name:
                news.attributes.categories.data[0]?.attributes?.title || null,
            },
          },
        };
      }),
      pageInfo: {
        offsetPagination: {
          total: data?.data?.search?.noticias?.data?.length || null,
        },
      },
    };
  } catch (err) {
    return null;
  }
};

export const getVideosBySearchTerm = async (
  termToSearch: string
): Promise<IGetVideosBySearchTermResponse> => {
  try {
    const { data } = await axiosPortalInstance({
      method: 'POST',
      data: {
        query: `
          query searchVideos {
            search(query: "${termToSearch}") {
              videos {
                data {
                  attributes {
                    title
                    slug
                    excerpt
                    video_id
                    publishedAt
                  }
                }
              }
            }
          }
        `,
      },
    });

    return {
      nodes: data?.data?.search?.videos?.data?.map(({ attributes }) => {
        return {
          title: attributes?.title || null,
          slug: attributes?.slug || null,
          excerpt: attributes?.excerpt || null,
          videoYoutubeIdUrl: getVideoThumbFromYoutube(attributes?.video_id),
          date: attributes?.publishedAt || null,
        };
      }),
      pageInfo: {
        offsetPagination: {
          total: data?.data?.search?.videos?.data?.length || null,
        },
      },
    };
  } catch (err) {
    return null;
  }
};

export const getTermsBySearchTerm = async (
  termToSearch: string
): Promise<IGetTermsBySearchTermResponse> => {
  try {
    const { data } = await axiosPortalInstance({
      method: 'POST',
      data: {
        query: `
        query search {
          search(query: "${termToSearch}") {
            glossarios {
              data {
                attributes {
                  term
                  slug
                }
              }
            }
          }
        } 
        `,
      },
    });

    return {
      nodes: data?.data?.search?.glossarios?.data?.map((termFound) => {
        return {
          title: termFound.attributes?.term || null,
          slug: termFound.attributes?.slug || null,
        };
      }),
      pageInfo: {
        offsetPagination: {
          total: data?.data?.search?.glossarios?.data?.length || null,
        },
      },
    };
  } catch (err) {
    return null;
  }
};

export const getPodcastsBySearchTerm = async (
  podcastToSearch: string
): Promise<IGetPodcastsBySearchTermResponse> => {
  try {
    const { data } = await axiosPortalInstance({
      method: 'POST',
      data: {
        query: `
          query search {
            search(query: "${podcastToSearch}") {
              podcasts {
                data {
                  attributes {
                    title
                    slug
                    excerpt
                    duration
                    publishedAt
                    thumbnail {
                      data {
                        attributes {
                          name
                          alternativeText
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
      },
    });

    return {
      nodes: data?.data?.search?.podcasts?.data?.map(({ attributes }) => {
        return {
          title: attributes?.title || null,
          slug: attributes?.slug || null,
          excerpt: attributes?.excerpt || null,
          featuredImage: {
            node: {
              altText:
                attributes.thumbnail.data?.attributes?.alternativeText || null,
              sourceUrl: attributes.thumbnail.data?.attributes?.url || null,
              title: attributes.thumbnail.data?.attributes?.name || null,
            },
          },
          tempoDeDuracao: attributes?.duration || null,
          date: attributes?.publishedAt || null,
        };
      }),
      pageInfo: {
        offsetPagination: {
          total: data?.data?.search?.podcasts?.data?.length || null,
        },
      },
    };
  } catch (err) {
    return null;
  }
};
